/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { IssueCreditNoteSchema, type IssueCreditNoteInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Invoice } from '../../lib/api/finance.js';
import { useCreditNotes, useIssueCreditNote } from '../../lib/queries/finance.js';
import { formatInr, rupeesToPaise } from '../../lib/utils/money.js';

interface CreditNoteFormProps {
  readonly invoice: Invoice;
  readonly onDone: () => void;
}

const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** Issue a credit note against a paid (locked) invoice + list prior ones (CR-010). */
export function CreditNoteForm({ invoice, onDone }: CreditNoteFormProps): JSX.Element {
  const { t } = useTranslation();
  const issue = useIssueCreditNote();
  const notes = useCreditNotes(invoice.id);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IssueCreditNoteInput>({ resolver: zodResolver(IssueCreditNoteSchema) });

  const submit = useCallback(
    async (values: IssueCreditNoteInput): Promise<void> => {
      await issue.mutateAsync({
        id: invoice.id,
        payload: { amountPaise: values.amountPaise, reason: values.reason },
      });
      onDone();
    },
    [issue, invoice.id, onDone],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <div className="space-y-4">
      <QueryState
        isLoading={notes.isLoading}
        isError={notes.isError}
        isEmpty={(notes.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('finance.creditNote.none')}
      >
        <ul className="space-y-1 rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-800">
          {(notes.data ?? []).map((n) => (
            <li key={n.id} className="flex justify-between text-slate-600 dark:text-slate-300">
              <span>
                {n.creditNumber} — {n.reason}
              </span>
              <span className="font-medium">{formatInr(n.amountPaise)}</span>
            </li>
          ))}
        </ul>
      </QueryState>

      <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
        <FormField
          id="cn-amount"
          type="number"
          label={t('finance.creditNote.amount')}
          autoComplete="off"
          inputMode="numeric"
          error={errors.amountPaise?.message}
          registration={register('amountPaise', {
            setValueAs: (v: string) => rupeesToPaise(Number(v)),
          })}
        />
        <div>
          <label htmlFor="cn-reason" className={LABEL_CLASS}>
            {t('finance.creditNote.reason')}
          </label>
          <textarea
            id="cn-reason"
            rows={3}
            aria-invalid={errors.reason !== undefined}
            className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            {...register('reason')}
          />
          {errors.reason !== undefined && (
            <p role="alert" className="mt-1 text-xs text-red-600">
              {errors.reason.message}
            </p>
          )}
        </div>
        {issue.isError && (
          <p role="alert" className="text-sm text-red-600">
            {t('common.saveError')}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting || issue.isPending}>
            {t('finance.creditNote.issue')}
          </Button>
        </div>
      </form>
    </div>
  );
}
